import { jsPDF } from "jspdf";
import { PDFDocument } from "pdf-lib";
import JSZip from "jszip";
import { db } from "./db";
import { listFiles, listVersions, fileDocName } from "./store";
import { compressImage, creatorText } from "./watermark";
import { blobToDataUrl, fmtBytes, stamp, docName, sha256Blob, ymd, kindOf, sniffType } from "./util";

const ascii = (s = "") =>
  String(s).replace(/[‘’]/g, "'").replace(/[“”]/g, '"').replace(/[–—]/g, "-").replace(/…/g, "...")
    .replace(/•/g, "\u00B7").replace(/★/g, "*").replace(/[^\x09\x0A\x0D\x20-\xFF]/g, "?");

export function matchNotes(all, { from, to, folderId, ids }) {
  return all.filter((n) => {
    if (ids) return ids.includes(n.id);
    const d = ymd(new Date(n.createdAt));
    return (!folderId || folderId === "all" || n.folderId === folderId) && (!from || d >= from) && (!to || d <= to);
  }).sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

export async function gatherNotes(filter) {
  const notes = matchNotes(await db.all("notes"), filter);
  const folders = Object.fromEntries((await db.all("folders")).map((f) => [f.id, f.name]));
  return Promise.all(notes.map(async (n) => ({
    note: n, folderName: folders[n.folderId] || "Folder",
    versions: await listVersions(n.id), files: await listFiles(n.id),
  })));
}

const W = 612, H = 792, M = 50, CW = W - M * 2;

function writer(doc) {
  const s = { y: M };
  s.ensure = (h) => { if (s.y + h > H - 60) { doc.addPage(); s.y = M; } };
  s.text = (t, { size = 10, style = "normal", color = [30, 30, 30], font = "helvetica", gap = 4 } = {}) => {
    doc.setFont(font, style); doc.setFontSize(size); doc.setTextColor(...color);
    for (const line of doc.splitTextToSize(ascii(t), CW)) {
      s.ensure(size + gap); doc.text(line, M, s.y + size); s.y += size + gap;
    }
  };
  s.space = (h) => { s.y += h; };
  return s;
}

async function addImage(doc, w, blob, level, maxH = 380) {
  try {
    const img = await compressImage(blob, level, level === "original" ? 2400 : 1600);
    const data = await blobToDataUrl(img.blob);
    let iw = CW, ih = (img.h / img.w) * iw;
    if (ih > maxH) { ih = maxH; iw = (img.w / img.h) * ih; }
    w.ensure(ih + 8);
    doc.addImage(data, "JPEG", M, w.y, iw, ih);
    w.y += ih + 6;
  } catch { w.text("[image could not be rendered]", { size: 8 }); }
}

function metaBox(doc, w, lines) {
  const h = lines.length * 11 + 12;
  w.ensure(h + 6);
  doc.setDrawColor(0, 0, 0); doc.setFillColor(246, 246, 246);
  doc.rect(M, w.y, CW, h, "FD");
  doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(20, 20, 20);
  lines.forEach((l, i) => doc.text(doc.splitTextToSize(ascii(l), CW - 12)[0], M + 6, w.y + 14 + i * 11));
  w.y += h + 8;
}

const noteTitle = (note, folderName) => note.title || `${folderName} - ${stamp(new Date(note.createdAt)).slice(0, 10)}`;
const RLABEL = { vendor: "Vendor", amount: "Amount", invoice: "Invoice/Receipt #", date: "Purchase date", carrier: "Carrier", tracking: "Tracking #", status: "Delivery status" };
const KIND = { text: "Text file", photo: "Photo", video: "Video", audio: "Audio recording", pdf: "PDF document", file: "File" };

function drawStar(doc, cx, cy, r) {
  const pts = Array.from({ length: 10 }, (_, i) => {
    const a = -Math.PI / 2 + (i * Math.PI) / 5, rr = i % 2 ? r * 0.4 : r;
    return [cx + rr * Math.cos(a), cy + rr * Math.sin(a)];
  });
  const segs = pts.slice(1).map((pt, i) => [pt[0] - pts[i][0], pt[1] - pts[i][1]]);
  doc.setFillColor(0, 0, 0);
  doc.lines(segs, pts[0][0], pts[0][1], [1, 1], "F", true);
}

function statusLine(doc, w) {
  w.ensure(16);
  const y = w.y + 6;
  doc.setDrawColor(0, 0, 0); doc.setLineWidth(1.2);
  doc.line(M, y, M + 3, y + 3); doc.line(M + 3, y + 3, M + 9, y - 4);
  doc.setLineWidth(0.5);
  doc.setFont("helvetica", "normal"); doc.setFontSize(8.5); doc.setTextColor(0, 0, 0);
  doc.text("Status: Timestamped & Hash-Sealed", M + 14, y + 3);
  w.y += 18;
}

function footers(doc, profile, watermarkFrom = 2) {
  const total = doc.getNumberOfPages();
  const wm = profile?.apply?.pdfs ? creatorText(profile) : "";
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    if (wm && i >= watermarkFrom) {
      doc.saveGraphicsState(); doc.setGState(new doc.GState({ opacity: profile.opacity * 0.5 }));
      doc.setFontSize(48); doc.setTextColor(150, 150, 150);
      doc.text(ascii(wm), W / 2, H / 2, { align: "center", angle: 30 });
      doc.restoreGraphicsState();
    }
    doc.setDrawColor(200, 200, 200); doc.setLineWidth(0.5); doc.line(M, H - 42, W - M, H - 42);
    drawStar(doc, M + 4, H - 30, 4.5);
    doc.setFontSize(7.5); doc.setTextColor(0, 0, 0); doc.setFont("helvetica", "normal");
    doc.text("StarVault \u00B7 by Sammy Star", M + 12, H - 27.5);
    doc.text(`Page ${i} of ${total}`, W - M, H - 27.5, { align: "right" });
  }
}

function embedPlaceholders(doc, w, f, ctx) {
  const src = ctx.pdfSrc[f.id];
  if (!src) { w.text("This PDF could not be opened for embedding (it may be password-protected). The original file is included in the .ZIP bundle.", { size: 8, color: [90, 90, 90] }); return; }
  const n = src.getPageCount();
  for (let k = 0; k < n; k++) {
    doc.addPage();
    ctx.placements.push({ id: f.id, k, page: doc.getCurrentPageInfo().pageNumber });
    doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(0, 0, 0);
    doc.text(ascii(`Attached PDF: ${f.name} - page ${k + 1} of ${n}`).slice(0, 110), M, M);
  }
  w.y = H;
}

async function resolveKinds(entries) {
  for (const e of entries) for (const f of e.files) {
    if (f.role === "original" && f.kind === "file") f.kind = kindOf(await sniffType(f.blob), f.name);
  }
}

async function loadPdfSources(entries) {
  const out = {};
  for (const e of entries) for (const f of e.files) {
    if (f.role !== "original" || f.kind !== "pdf") continue;
    try { out[f.id] = await PDFDocument.load(await f.blob.arrayBuffer(), { ignoreEncryption: true }); } catch { /* not embeddable */ }
  }
  return out;
}

async function embedPdfPages(bytes, ctx) {
  if (!ctx.placements.length) return new Blob([bytes], { type: "application/pdf" });
  const out = await PDFDocument.load(bytes);
  const byFile = {};
  ctx.placements.forEach((p) => { (byFile[p.id] = byFile[p.id] || []).push(p); });
  for (const [id, list] of Object.entries(byFile)) {
    let embs;
    try { embs = await out.embedPdf(ctx.pdfSrc[id], list.map((p) => p.k)); } catch { continue; }
    list.forEach((p, i) => {
      const e = embs[i], page = out.getPage(p.page - 1);
      const top = 66, bottom = 52, aw = W - 2 * M, ah = H - top - bottom;
      const s = Math.min(aw / e.width, ah / e.height);
      const pw = e.width * s, ph = e.height * s;
      page.drawPage(e, { x: (W - pw) / 2, y: bottom + (ah - ph) / 2, width: pw, height: ph });
    });
  }
  return new Blob([await out.save()], { type: "application/pdf" });
}

async function renderEntry(doc, w, e, ctx, i) {
  const { note, folderName, versions, files } = e;
  const hashes = ctx.includeHashes;
  if (!note.body?.trim() && files.some((f) => f.role === "original" && f.kind !== "audio")) w.ensure(300);
  w.text(`${i + 1}. ${noteTitle(note, folderName)}`, { size: 15, style: "bold", color: [0, 0, 0] });
  const r = note.receipt || {};
  const ref = note.ref || note.summary?.ref;
  const meta = [
    `Folder: ${folderName}`, `Created: ${stamp(new Date(note.createdAt))}`, ref && `${note.folderId === "dispute-legal-log" ? "Claim # / File #" : "Ref/File #"}: ${ref}`, note.context && `Context: ${note.context}`, !note.apptAt && !note.deadline && !note.healthDate && note.scheduledFor && `${note.entryType || "Calendar"} date: ${note.scheduledFor}`, `Versions: ${versions.length}`,
    note.apptAt && `When: ${note.apptAt.replace("T", " ")}`, note.deadline && `Court date / deadline: ${note.deadline}`, note.healthDate && `Date: ${note.healthDate}`, note.tags?.length && `Categories: ${note.tags.join(", ")}`, note.location && `Location: ${note.location}`,
    ...Object.entries(RLABEL).filter(([k]) => r[k]).map(([k, l]) => `${l}: ${k === "amount" ? "$" : ""}${r[k]}`),
  ].filter(Boolean);
  doc.setFont("helvetica", "normal"); doc.setFontSize(8);
  const rows = meta.reduce((acc, item) => {
    const last = acc[acc.length - 1];
    if (last && doc.getTextWidth(ascii(`${last}   |   ${item}`)) <= CW) acc[acc.length - 1] = `${last}   |   ${item}`; else acc.push(item);
    return acc;
  }, []);
  rows.forEach((row) => w.text(row, { size: 8, color: [80, 80, 80] }));
  w.space(6);
  if (note.body?.trim()) { w.text(note.body, { size: 10 }); w.space(6); }
  for (const f of files.filter((x) => x.role === "original")) {
    const kids = files.filter((x) => x.parentId === f.id);
    if (f.kind === "photo") {
      await addImage(doc, w, (kids.find((k) => k.role === "stamped") || f).blob, ctx.level);
    } else if (f.kind === "video") {
      const th = kids.find((k) => k.role === "thumb");
      if (th) await addImage(doc, w, th.blob, ctx.level, 260);
      for (const fr of kids.filter((k) => k.role === "frame")) await addImage(doc, w, fr.blob, ctx.level, 220);
    }
    metaBox(doc, w, [
      `${KIND[f.kind] || "File"}: ${f.name}`,
      `Saved as: ${fileDocName(f, note, folderName, ctx.profile)}`,
      `Size: ${fmtBytes(f.size)}   |   Attached: ${stamp(new Date(f.createdAt))}`,
      hashes && `SHA-256: ${f.hash}`,
    ].filter(Boolean));
    if (!hashes) statusLine(doc, w);
    if (f.kind === "pdf") embedPlaceholders(doc, w, f, ctx);
    if (f.kind === "text") { w.text((await f.blob.text()).slice(0, 20000), { size: 9, color: [30, 30, 30] }); w.space(8); }
  }
  if (versions.length) {
    w.ensure(40);
    w.text("Version History", { size: 9, style: "bold", color: [0, 0, 0] });
    versions.forEach((v) => w.text(
      `Version ${v.n}  |  ${v.localTime}${v.creatorStamp ? `  |  ${v.creatorStamp}` : ""}  |  ${hashes ? `SHA-256 ${v.hash}` : "Timestamped & Hash-Sealed"}`,
      { size: 7.5, color: [60, 60, 60], gap: 3 },
    ));
  }
  w.space(18);
}

export async function buildPdf(entries, ctx) {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  doc.setProperties({ title: ctx.title, author: ctx.profile.fullName || "", creator: "StarVault by Sammy Star" });
  await resolveKinds(entries);
  ctx = { ...ctx, pdfSrc: await loadPdfSources(entries), placements: [] };
  const p = ctx.profile;
  const FIRST = 20, PER = 34;
  const tocPages = entries.length <= FIRST ? 1 : 1 + Math.ceil((entries.length - FIRST) / PER);
  for (let i = 1; i < tocPages; i++) doc.addPage();
  doc.addPage();
  const w = writer(doc);
  const pages = [];
  for (const [i, e] of entries.entries()) {
    w.ensure(80);
    pages.push(doc.getCurrentPageInfo().pageNumber);
    await renderEntry(doc, w, e, ctx, i);
  }
  w.ensure(200);
  w.text("Creator Certification", { size: 14, style: "bold", color: [0, 0, 0] });
  w.text(`I, ${p.fullName || "the undersigned"}, certify that the ${entries.length} entries above were recorded in StarVault, that each saved version is timestamped and SHA-256 hash-sealed, and that attached media is reproduced from the stored originals.`, { size: 10 });
  w.text("Integrity note: hashes are computed on the recording device. They show whether content changed after sealing, but they are not independent third-party notarization or a trusted timestamp.", { size: 8, color: [0, 0, 0] });
  w.space(10);
  if (p.signature) {
    try { w.ensure(70); doc.addImage(p.signature, "PNG", M, w.y, 180, 60); w.y += 64; } catch { /* skip */ }
  }
  doc.setDrawColor(0, 0, 0); doc.line(M, w.y, M + 220, w.y); w.space(4);
  w.text(`${p.fullName || "Signature"}${p.initials ? ` (${p.initials})` : ""}  |  Signed ${stamp()}`, { size: 9 });

  doc.setPage(1);
  doc.setTextColor(0, 0, 0); doc.setFont("helvetica", "bold"); doc.setFontSize(30);
  const titleLines = doc.splitTextToSize(ascii(ctx.title), CW);
  doc.text(titleLines, M, M + 34);
  let y = M + 34 + titleLines.length * 34;
  doc.setFont("helvetica", "normal"); doc.setFontSize(11); doc.setTextColor(60, 60, 60);
  [ctx.rangeLabel, p.fullName && `Prepared by: ${p.fullName}${p.initials ? ` (${p.initials})` : ""}`, `Compiled: ${stamp()}`, `Entries: ${entries.length}`]
    .filter(Boolean).forEach((l) => { doc.text(ascii(l), M, y); y += 16; });
  y += 10;
  y += 24;
  doc.setTextColor(0, 0, 0); doc.setFont("helvetica", "bold"); doc.setFontSize(13);
  doc.text("Table of Contents", M, y); y += 22;
  doc.setFont("helvetica", "normal"); doc.setFontSize(9.5);
  entries.forEach((e, idx) => {
    if (idx === FIRST || (idx > FIRST && (idx - FIRST) % PER === 0)) { doc.setPage(2 + Math.floor((idx - FIRST) / PER)); y = M + 10; }
    doc.text(ascii(`${idx + 1}.  ${stamp(new Date(e.note.createdAt)).slice(0, 16)}   ${noteTitle(e.note, e.folderName)}`).slice(0, 90), M, y);
    doc.text(String(pages[idx]), W - M, y, { align: "right" });
    y += 19;
  });
  footers(doc, p, tocPages + 1);
  return embedPdfPages(doc.output("arraybuffer"), ctx);
}

export function buildTextPdf(text) {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const w = writer(doc);
  const [first, ...rest] = text.split("\n");
  w.text(first, { size: 18, style: "bold", color: [0, 0, 0] });
  w.space(6);
  w.text(rest.join("\n"), { size: 10, color: [20, 20, 20] });
  footers(doc, null);
  return doc.output("blob");
}

function unique(used, name) {
  let out = name, i = 2;
  while (used.has(out.toLowerCase())) out = name.replace(/(\.[^.]+)?$/, (ext) => `-${i++}${ext}`);
  used.add(out.toLowerCase());
  return out;
}

export async function buildBundle(entries, ctx, pdfName) {
  const zip = new JSZip();
  const pdf = await buildPdf(entries, ctx);
  zip.file(pdfName, pdf);
  const media = zip.folder("Evidence_Media");
  const used = new Set();
  const rows = [["filename", "role", "note", "attached", "bytes", "sha256"]];
  for (const e of entries) {
    for (const f of e.files) {
      let blob = f.blob, hash = f.hash;
      if (f.role !== "original" && ctx.level !== "original") {
        blob = (await compressImage(f.blob, ctx.level)).blob;
        hash = await sha256Blob(blob);
      }
      const name = unique(used, fileDocName(f, e.note, e.folderName, ctx.profile));
      media.file(name, blob);
      rows.push([name, f.role, e.note.title, f.createdAt, blob.size, hash]);
    }
  }
  media.file("_media_manifest.csv", rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n"));
  return zip.generateAsync({ type: "blob" });
}

export const compileName = (ctx, ext) =>
  docName({ profile: ctx.profile, folderName: "Merged", title: ctx.title, ref: "", date: new Date(), ext });
